export interface ContentSettings {
  // Auth - sign up / sign in
  signUpLabel: string;
  signUpTitle: string;
  signUpDescription: string;
  signInLabel: string;
  signInTitle: string;
  signInDescription: string;
  adminSignInLabel: string;
  adminSignInTitle: string;
  adminSignInDescription: string;

  // Auth - forgot / reset password
  forgotPasswordLabel: string;
  forgotPasswordTitle: string;
  forgotPasswordDescription: string;
  forgotPasswordSuccessLabel: string;
  forgotPasswordSuccessTitle: string;
  forgotPasswordSuccessDescription: string;
  forgotPasswordResendButtonText: string;
  resetPasswordLabel: string;
  resetPasswordTitle: string;
  resetPasswordDescription: string;
  resetPasswordSuccessLabel: string;
  resetPasswordSuccessTitle: string;
  resetPasswordSuccessDescription: string;
  resetPasswordSuccessButtonText: string;

  // Auth - class registration deep link banner
  classRegistrationAuthSignIn: string;
  classRegistrationAuthSignUp: string;

  // Homepage
  homeKicker: string;
  homeTitle: string;
  homeDescription: string;
  homePrimaryCtaLabel: string;
  homeSecondaryCtaLabel: string;
  homeSeasonKicker: string;
  homeSeasonDescription: string;
  homeSeasonFallbackTitle: string;
  homeSeasonCtaLabel: string;
  homeSelectedClassesTitle: string;
  homeSelectedClassesDescription: string;
  homeSelectedClassesCtaLabel: string;
  homeSelectedClassesEmptyTitle: string;
  homeSelectedClassesEmptyDescription: string;
  homeInstructorsTitle: string;
  homeInstructorsDescription: string;
  homeInstructorsCtaLabel: string;
  homeInstructorsEmptyTitle: string;
  homeInstructorsEmptyDescription: string;
  homeLocationsTitle: string;
  homeLocationsDescription: string;
  homeLocationsCtaLabel: string;
  homeLocationsEmptyTitle: string;
  homeLocationsEmptyDescription: string;
  homeStoryTitle: string;
  homeStoryBody: string;
  homeCloseKicker: string;
  homeCloseTitle: string;
  homeCloseDescription: string;
  homeCloseCtaLabel: string;
  homeCloseSecondaryCtaLabel: string;

  // Contact
  contactLabel: string;
  contactTitle: string;
  contactDescription: string;
  contactSuccessTitle: string;
  contactSuccessDescription: string;
  contactSuccessButtonText: string;

  // Classes listing
  classesSubtitle: string;
  classesTitle: string;
  classesDescription: string;
  classesNoSeasonsTitle: string;
  classesNoSeasonsDescription: string;
  classesNoClassesTitle: string;
  classesNoClassesDescription: string;
  classesNoResultsTitle: string;
  classesNoResultsSuggestions: string;

  // Teachers listing
  teachersSubtitle: string;
  teachersTitle: string;
  teachersDescription: string;
  teachersEmptyTitle: string;
  teachersEmptyDescription: string;

  // Locations listing
  locationsSubtitle: string;
  locationsTitle: string;
  locationsDescription: string;
  locationsEmptyTitle: string;
  locationsEmptyDescription: string;

  // Terms
  termsSubtitle: string;
  termsTitle: string;
  termsDescription: string;
  termsFooterNote: string;
  termsSignUpPrompt: string;

  // Member portal - class registration
  classRegistrationImportantInfoTitle: string;
  classRegistrationImportantInfo: string;

  // Member portal - sidebar navigation labels
  portalNavStudentsLabel: string;
  portalNavAppointmentsLabel: string;
  portalNavMakeupsLabel: string;
  portalNavTermsLabel: string;

  // Microsite - shared list fetch errors
  micrositeListFetchErrorTitle: string;
  micrositeListFetchErrorMessage: string;
  micrositeListFetchErrorRetryButton: string;

  // Microsite - locations listing server error
  locationsListingFetchErrorTitle: string;
  locationsListingFetchErrorMessage: string;

  // Microsite - terms empty state
  termsEmptyTitle: string;
  termsEmptyDescriptionPublic: string;
  termsEmptyDescriptionPortal: string;

  // Microsite - contact auth CTA
  contactAuthCtaDescription: string;
  contactAuthCtaSignUpButton: string;
  contactAuthCtaSignInButton: string;
}

/** Shown in admin Content Settings next to fields that support placeholders. */
export const CONTENT_SETTINGS_PLACEHOLDER_TIP =
  "Tip: Use {studioName}, {email}, or {className} where noted.";

export const CONTENT_SETTINGS_EMAIL_TIP =
  "Tip: Use {email} to insert the business contact email (mailto link when available).";

export const CONTENT_SETTINGS_USER_EMAIL_TIP =
  "Tip: Use {userEmail} to insert the email address used for the password reset request.";

export const CONTENT_SETTINGS_STUDIO_NAME_TIP =
  "Tip: Use {studioName} to insert your business display name.";

export const CONTENT_SETTINGS_CLASS_NAME_TIP =
  "Tip: Use {className} for the class name (auth banner only).";

export const CONTENT_SETTINGS_SEASON_NAME_TIP =
  "Tip: Use {seasonName} to insert the featured season name.";

/** Multi-tenant placeholder defaults (fallback when API fails or field not set). */
export const DEFAULT_CONTENT_SETTINGS: ContentSettings = {
  signUpLabel: "Join Us",
  signUpTitle: "Create Your Account",
  signUpDescription:
    "Create an account to browse the class schedule, register for sessions, and manage enrollments online.",
  signInLabel: "Welcome Back",
  signInTitle: "Sign In to Your Account",
  signInDescription:
    "Sign in to manage your account, view schedules, and register for classes. New here? Explore our offerings and create an account when you are ready.",
  adminSignInLabel: "Admin Portal",
  adminSignInTitle: "Sign In to Admin Dashboard",
  adminSignInDescription:
    "Access the admin dashboard to manage classes, students, instructors, schedules, and appointments. Use your staff credentials to continue.",

  forgotPasswordLabel: "Reset Password",
  forgotPasswordTitle: "Forgot your password?",
  forgotPasswordDescription:
    "No worries - enter your email and we'll send you instructions to reset your password.",
  forgotPasswordSuccessLabel: "Check your email",
  forgotPasswordSuccessTitle: "Email sent",
  forgotPasswordSuccessDescription:
    "We've sent a password reset link to {userEmail}. Please check your inbox and follow the instructions.",
  forgotPasswordResendButtonText: "Didn't get an email? Resend link",
  resetPasswordLabel: "Reset Password",
  resetPasswordTitle: "Create new password",
  resetPasswordDescription:
    "Enter your new password below. Use at least 8 characters with one uppercase letter and one special character.",
  resetPasswordSuccessLabel: "Success",
  resetPasswordSuccessTitle: "Password reset successful",
  resetPasswordSuccessDescription: "You can now sign in with your new password.",
  resetPasswordSuccessButtonText: "Go to sign in",

  classRegistrationAuthSignIn: "Sign in to register for {className}.",
  classRegistrationAuthSignUp: "Create an account to register for {className}.",

  homeKicker: "Welcome",
  homeTitle: "Classes, instructors, and registration in one place",
  homeDescription:
    "Browse this season at {studioName}, meet the instructors, and register when you are ready.",
  homePrimaryCtaLabel: "Browse classes",
  homeSecondaryCtaLabel: "Create account",
  homeSeasonKicker: "This season",
  homeSeasonDescription:
    "{seasonName} is on the calendar. Open the schedule to see days, times, and locations.",
  homeSeasonFallbackTitle: "Classes this season",
  homeSeasonCtaLabel: "View season classes",
  homeSelectedClassesTitle: "Selected classes",
  homeSelectedClassesDescription:
    "A few places to start. Open a class for the full schedule and registration.",
  homeSelectedClassesCtaLabel: "All classes",
  homeSelectedClassesEmptyTitle: "Classes will appear here",
  homeSelectedClassesEmptyDescription:
    "Check back soon, or contact us at {email} if you have a question about registration.",
  homeInstructorsTitle: "Instructors",
  homeInstructorsDescription: "The people who teach at {studioName}.",
  homeInstructorsCtaLabel: "All instructors",
  homeInstructorsEmptyTitle: "Instructors will appear here",
  homeInstructorsEmptyDescription:
    "Check back soon, or contact us at {email} if you have a question.",
  homeLocationsTitle: "Locations",
  homeLocationsDescription: "Where classes meet.",
  homeLocationsCtaLabel: "View locations",
  homeLocationsEmptyTitle: "Locations will appear here",
  homeLocationsEmptyDescription:
    "Check back soon, or contact us at {email} if you have a question.",
  homeStoryTitle: "A simpler way to enroll",
  homeStoryBody:
    "{studioName} keeps the class list, instructors, and registration in one place. Families can see what is offered, pick a time that works, and enroll without paper forms.\n\nYour account holds students, enrollments, and the schedule together — so you are not chasing updates across emails and flyers.\n\nIf you want help choosing a class, send a note. We will point you in the right direction.",
  homeCloseKicker: "Questions",
  homeCloseTitle: "Not sure which class fits?",
  homeCloseDescription:
    "Tell us what you are looking for and we will help you choose. Create an account when you are ready to register.",
  homeCloseCtaLabel: "Contact us",
  homeCloseSecondaryCtaLabel: "Create account",

  contactLabel: "Get in Touch",
  contactTitle: "Contact Us",
  contactDescription:
    "Have a question or want to learn more about our classes? Send us a message and we'll get back to you as soon as possible.",
  contactSuccessTitle: "Message Sent!",
  contactSuccessDescription:
    "Thank you for contacting us. We'll get back to you as soon as possible.",
  contactSuccessButtonText: "Send Another Message",

  classesSubtitle: "Explore our classes",
  classesTitle: "Classes",
  classesDescription:
    "Browse classes offered this season. Use search and filters to narrow by day, time, or location. Questions about which class to choose? Contact us at {email}.",
  classesNoSeasonsTitle: "There are currently no classes available this season.",
  classesNoSeasonsDescription: "Please check back later or contact us at {email}.",
  classesNoClassesTitle: "There are currently no classes available.",
  classesNoClassesDescription: "Please check back later or contact us at {email}.",
  classesNoResultsTitle: "No classes match your selected filters.",
  classesNoResultsSuggestions:
    "Try removing some filters,Check your spelling,Browse all classes",

  teachersSubtitle: "Our team",
  teachersTitle: "Instructors",
  teachersDescription:
    "Browse our instructor profiles. Open a profile to learn more and see the classes they teach.",
  teachersEmptyTitle: "No instructors listed yet",
  teachersEmptyDescription:
    "Instructor profiles will appear here once they are published.",

  locationsSubtitle: "Our locations",
  locationsTitle: "Locations",
  locationsDescription:
    "Explore where classes are held. Select a location for directions, contact details, and classes offered there.",
  locationsEmptyTitle: "No locations listed yet",
  locationsEmptyDescription:
    "Locations will appear here once they are added.",

  termsSubtitle: "Legal information",
  termsTitle: "Waivers",
  termsDescription:
    "Please review our policies and waivers before enrolling in classes or participating in activities.",
  termsFooterNote:
    "Questions about these policies? Contact us at {email}.",
  termsSignUpPrompt:
    "Creating an account? You'll review and accept these policies during {signUp}.",

  classRegistrationImportantInfoTitle: "Important Information",
  classRegistrationImportantInfo:
    "• Review payment and enrollment requirements for your location before registering.\n• Read prerequisite and level notes carefully. Some classes may require instructor approval.\n• Placement or level may be adjusted to ensure a safe, appropriate class experience.\n• Each participant must be added as a Student on your account before they can enroll in a class.\n• If a class is full, contact us to join the waitlist and specify which class you want.",

  portalNavStudentsLabel: "Students",
  portalNavAppointmentsLabel: "Private Lessons",
  portalNavMakeupsLabel: "Makeup requests",
  portalNavTermsLabel: "Waivers",

  micrositeListFetchErrorTitle: "Couldn't load this page",
  micrositeListFetchErrorMessage:
    "Something went wrong while fetching data. Please try again.",
  micrositeListFetchErrorRetryButton: "Try again",

  locationsListingFetchErrorTitle: "Couldn't load locations",
  locationsListingFetchErrorMessage:
    "Something went wrong while loading studio locations. Please try again.",

  termsEmptyTitle: "No waivers available right now",
  termsEmptyDescriptionPublic:
    "Policies and waivers will appear here once your studio publishes them.",
  termsEmptyDescriptionPortal:
    "Your signed and pending waivers will appear here once policies are available.",

  contactAuthCtaDescription:
    "Create an account to register for classes online, or sign in if you already have one.",
  contactAuthCtaSignUpButton: "Create account",
  contactAuthCtaSignInButton: "Sign in",
};
